import { useCallback, useEffect, useRef, useState } from "react";

import {
  AirshowHeartbeatCorruptResponse,
  airshowHeartbeatClient,
  type AirshowHeartbeatClient,
} from "./airshowHeartbeatApi";
import {
  isHeartbeatReportReady,
  isHeartbeatTerminal,
  type AirshowHeartbeatModelsPage,
  type AirshowHeartbeatReport,
  type AirshowHeartbeatRun,
} from "./airshowHeartbeatContract";
import { AirshowHeartbeatPoller } from "./airshowHeartbeatPolling";

export type HeartbeatUiPhase = "idle" | "loading" | "ready" | "error" | "corrupt";
export interface AirshowHeartbeatState {
  phase: HeartbeatUiPhase;
  status: AirshowHeartbeatRun | null;
  report: AirshowHeartbeatReport | null;
  page: AirshowHeartbeatModelsPage | null;
  message: string | null;
  pollMessage: string | null;
  busy: "start" | "cancel" | "report" | "page" | null;
}
export interface AirshowHeartbeatActions {
  startFull(): Promise<void>;
  cancel(): Promise<void>;
  rerunFailures(): Promise<void>;
  loadPage(cursor: number): Promise<void>;
}
export interface UseAirshowHeartbeatInput {
  active: boolean;
  projectId: string;
  catalogRevision: string;
  archiveSha256: string;
}

const INITIAL: AirshowHeartbeatState = {
  phase: "idle", status: null, report: null, page: null,
  message: null, pollMessage: null, busy: null,
};

function failure(error: unknown): Pick<AirshowHeartbeatState, "phase" | "message"> {
  return error instanceof AirshowHeartbeatCorruptResponse
    ? { phase: "corrupt", message: "Heartbeat evidence was rejected because its response contract is corrupt." }
    : { phase: "error", message: error instanceof Error ? error.message : "Airshow heartbeat is unavailable." };
}

export function useAirshowHeartbeat(
  input: UseAirshowHeartbeatInput,
  client: AirshowHeartbeatClient = airshowHeartbeatClient,
): AirshowHeartbeatState & AirshowHeartbeatActions {
  const [view, setView] = useState<AirshowHeartbeatState>(INITIAL);
  const [runId, setRunId] = useState<string | null>(null);
  const currentController = useRef<AbortController | null>(null);
  const actionController = useRef<AbortController | null>(null);
  const detailController = useRef<AbortController | null>(null);
  const pageController = useRef<AbortController | null>(null);
  const poller = useRef<AirshowHeartbeatPoller | null>(null);
  const actionActive = useRef(false);
  const terminalLoaded = useRef<string | null>(null);
  const terminalResume = useRef<AirshowHeartbeatRun | null>(null);
  const identity = `${input.projectId}\u001f${input.catalogRevision}\u001f${input.archiveSha256}`;
  const identityRef = useRef(identity);

  const loadTerminal = useCallback(async (status: AirshowHeartbeatRun) => {
    if (terminalLoaded.current === status.run_id) return;
    terminalLoaded.current = status.run_id;
    detailController.current?.abort();
    const controller = new AbortController();
    detailController.current = controller;
    setView((current) => ({ ...current, busy: "report", message: null }));
    try {
      const [report, page] = await Promise.all([
        client.report({ projectId: input.projectId, runId: status.run_id, signal: controller.signal }),
        client.models({ projectId: input.projectId, runId: status.run_id, cursor: 0, limit: 25, signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return;
      setView((current) => ({ ...current, phase: "ready", status, report, page, busy: null, message: null }));
    } catch (error) {
      if (controller.signal.aborted) return;
      terminalLoaded.current = null;
      setView((current) => ({ ...current, ...failure(error), status, busy: null }));
    } finally {
      if (detailController.current === controller) detailController.current = null;
    }
  }, [client, input.projectId]);

  useEffect(() => {
    if (identityRef.current === identity) return;
    identityRef.current = identity;
    currentController.current?.abort(); actionController.current?.abort();
    detailController.current?.abort(); pageController.current?.abort(); poller.current?.stop();
    terminalLoaded.current = null;
    terminalResume.current = null;
    actionActive.current = false;
    setRunId(null);
    setView(INITIAL);
  }, [identity]);

  useEffect(() => {
    if (input.active) return;
    if (detailController.current && view.status && isHeartbeatReportReady(view.status) && !view.report) {
      terminalResume.current = view.status;
      terminalLoaded.current = null;
    }
    currentController.current?.abort(); actionController.current?.abort();
    detailController.current?.abort(); pageController.current?.abort(); poller.current?.stop();
    actionActive.current = false;
    setView((current) => current.busy === null ? current : { ...current, busy: null });
  }, [input.active, view.report, view.status]);

  useEffect(() => {
    if (!input.active || !terminalResume.current) return;
    const status = terminalResume.current;
    terminalResume.current = null;
    void loadTerminal(status);
  }, [input.active, loadTerminal]);

  useEffect(() => {
    if (!input.active || actionActive.current || view.status) return;
    currentController.current?.abort();
    const controller = new AbortController();
    currentController.current = controller;
    setView((current) => ({ ...current, phase: "loading", message: null }));
    void client.current({ projectId: input.projectId, signal: controller.signal }).then((status) => {
      if (controller.signal.aborted) return;
      if (!status) {
        setView((current) => ({ ...current, phase: "idle", busy: null }));
        return;
      }
      if (status.project_id !== input.projectId) throw new AirshowHeartbeatCorruptResponse();
      setView((current) => ({ ...current, phase: "ready", status, message: null }));
      if (isHeartbeatReportReady(status)) void loadTerminal(status);
      else setRunId(status.run_id);
    }).catch((error: unknown) => {
      if (controller.signal.aborted) return;
      setView((current) => ({ ...current, ...failure(error), busy: null }));
    });
    return () => controller.abort();
  }, [client, input.active, input.projectId, loadTerminal, view.status]);

  useEffect(() => {
    if (!input.active || !runId) return;
    const scheduler = new AirshowHeartbeatPoller({
      poll: ({ etag, runId: activeRunId, signal }) => client.status({
        projectId: input.projectId, runId: activeRunId, etag, signal,
      }),
      isVisible: () => document.visibilityState === "visible",
      onStatus: (status) => setView((current) => ({
        ...current, phase: "ready", status, pollMessage: null, message: null,
      })),
      onTerminal: (status) => {
        setRunId(null);
        void loadTerminal(status);
      },
      onError: (error, count) => setView((current) => ({
        ...current,
        phase: error instanceof AirshowHeartbeatCorruptResponse ? "corrupt" : current.phase,
        pollMessage: error instanceof AirshowHeartbeatCorruptResponse
          ? "A corrupt compact response was rejected; retrying without accepting it."
          : `Heartbeat update failed; retrying with backoff (${count}).`,
      })),
    });
    poller.current = scheduler;
    scheduler.start(runId);
    const visible = () => scheduler.visibilityChanged();
    document.addEventListener("visibilitychange", visible);
    return () => {
      document.removeEventListener("visibilitychange", visible);
      scheduler.stop();
      if (poller.current === scheduler) poller.current = null;
    };
  }, [client, input.active, input.projectId, loadTerminal, runId]);

  useEffect(() => () => {
    currentController.current?.abort(); actionController.current?.abort();
    detailController.current?.abort(); pageController.current?.abort(); poller.current?.stop();
  }, []);

  const start = useCallback(async (scope: "all" | "failures-from", priorRunId: string | null) => {
    actionActive.current = true;
    currentController.current?.abort(); actionController.current?.abort();
    detailController.current?.abort(); pageController.current?.abort(); poller.current?.stop();
    setRunId(null); terminalLoaded.current = null;
    terminalResume.current = null;
    const controller = new AbortController(); actionController.current = controller;
    setView({ ...INITIAL, phase: "loading", busy: "start" });
    try {
      const status = await client.start({
        projectId: input.projectId, scope, priorRunId,
        catalogRevision: input.catalogRevision, archiveSha256: input.archiveSha256,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setView({ ...INITIAL, phase: "ready", status });
      if (isHeartbeatReportReady(status)) void loadTerminal(status);
      else setRunId(status.run_id);
    } catch (error) {
      if (!controller.signal.aborted) setView({ ...INITIAL, ...failure(error) });
    } finally {
      if (actionController.current === controller) actionController.current = null;
      actionActive.current = false;
    }
  }, [client, input.archiveSha256, input.catalogRevision, input.projectId, loadTerminal]);

  const cancel = useCallback(async () => {
    const status = view.status;
    if (!status || isHeartbeatTerminal(status.state)) return;
    actionController.current?.abort();
    const controller = new AbortController(); actionController.current = controller;
    setView((current) => ({ ...current, busy: "cancel", message: null }));
    try {
      const next = await client.cancel({ projectId: input.projectId, runId: status.run_id, signal: controller.signal });
      if (controller.signal.aborted) return;
      setView((current) => ({ ...current, phase: "ready", status: next, busy: null }));
      if (isHeartbeatReportReady(next)) { setRunId(null); void loadTerminal(next); }
    } catch (error) {
      if (!controller.signal.aborted) setView((current) => ({ ...current, ...failure(error), busy: null }));
    }
  }, [client, input.projectId, loadTerminal, view.status]);

  const loadPage = useCallback(async (cursor: number) => {
    const status = view.status;
    if (!status || !isHeartbeatTerminal(status.state)) return;
    pageController.current?.abort(); const controller = new AbortController(); pageController.current = controller;
    setView((current) => ({ ...current, busy: "page", message: null }));
    try {
      const page = await client.models({ projectId: input.projectId, runId: status.run_id, cursor, limit: 25, signal: controller.signal });
      if (!controller.signal.aborted) setView((current) => ({ ...current, phase: "ready", page, busy: null }));
    } catch (error) {
      if (!controller.signal.aborted) setView((current) => ({ ...current, ...failure(error), busy: null }));
    }
  }, [client, input.projectId, view.status]);

  return {
    ...view,
    startFull: () => start("all", null),
    rerunFailures: () => view.status ? start("failures-from", view.status.run_id) : Promise.resolve(),
    cancel,
    loadPage,
  };
}
